import { getCountries } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";

/** ISO 3166-1 alpha-2 — same format EasyBill expects for `country`. */
export type IsoCountryCode = CountryCode;

const displayNames = new Intl.DisplayNames(["de"], { type: "region" });

export function isoCountryDisplayName(iso: IsoCountryCode): string {
  return displayNames.of(iso) ?? iso;
}

export function isoCountryFlagEmoji(iso: IsoCountryCode): string {
  return [...iso.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

const PINNED: IsoCountryCode[] = ["DE", "AT", "CH", "US", "GB"];

export const ISO_COUNTRY_CODES: IsoCountryCode[] = (() => {
  const rest: IsoCountryCode[] = getCountries()
    .filter((c) => !PINNED.includes(c))
    .sort((a, b) =>
      isoCountryDisplayName(a).localeCompare(isoCountryDisplayName(b), "de"),
    );
  return [...PINNED, ...rest];
})();

const VALID_ISO_COUNTRIES: ReadonlySet<string> = new Set<string>(
  ISO_COUNTRY_CODES,
);

export function isValidIsoCountryCode(raw: string): boolean {
  return VALID_ISO_COUNTRIES.has(raw.trim().toUpperCase());
}

export function normalizeIsoCountryCode(raw: string): IsoCountryCode {
  const code: string = raw.trim().toUpperCase();
  if (!VALID_ISO_COUNTRIES.has(code)) {
    throw new Error("Bitte ein gültiges Land auswählen.");
  }
  return code as IsoCountryCode;
}

let nameToIso: ReadonlyMap<string, IsoCountryCode> | undefined;

function countryNameKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Ländername (deutsch oder englisch, z. B. „Deutschland“, „Germany“) oder
 * ISO-Code → ISO 3166-1 alpha-2. `undefined`, wenn nichts passt. Für
 * Fremdsysteme, die nur Codes annehmen (Impower `AddressDto.country`).
 */
export function countryToIsoCode(raw: string | undefined): IsoCountryCode | undefined {
  if (raw === undefined) return undefined;
  const trimmed: string = raw.trim();
  if (trimmed.length === 0) return undefined;
  if (isValidIsoCountryCode(trimmed)) return trimmed.toUpperCase() as IsoCountryCode;
  if (nameToIso === undefined) {
    const englishNames = new Intl.DisplayNames(["en"], { type: "region" });
    const map = new Map<string, IsoCountryCode>();
    for (const code of ISO_COUNTRY_CODES) {
      map.set(countryNameKey(isoCountryDisplayName(code)), code);
      const english: string | undefined = englishNames.of(code);
      if (english) map.set(countryNameKey(english), code);
    }
    nameToIso = map;
  }
  return nameToIso.get(countryNameKey(trimmed));
}
